/**
 * Night-mode colour tokens. Defaults live here; Super Admin picks a named
 * preset so tenants are not hardcoded to one navy.
 */

export const NIGHT_PALETTE_KEY = 'saas_night_palette';
export const LOADER_APPEARANCE_KEY = 'saas_loader_appearance';

export type NightPaletteId = 'professional' | 'deep' | 'midnight';
export type LoaderAppearance = 'bright' | 'match_theme';

export interface NightPaletteTokens {
  canvas: string;
  surface: string;
  muted: string;
  elevated: string;
  border: string;
  text: string;
  mutedText: string;
  accent: string;
}

export const NIGHT_PALETTE_IDS: readonly NightPaletteId[] = ['professional', 'deep', 'midnight'];

export const NIGHT_PALETTE_LABELS: Record<NightPaletteId, string> = {
  professional: 'Professional (lifted navy)',
  deep: 'Deep charcoal',
  midnight: 'Midnight teal',
};

export const LOADER_APPEARANCES: readonly LoaderAppearance[] = ['bright', 'match_theme'];

export const LOADER_APPEARANCE_LABELS: Record<LoaderAppearance, string> = {
  bright: 'Bright (recommended)',
  match_theme: 'Match workspace theme',
};

/** Default night — readable surfaces, not near-black. */
export const NIGHT_PALETTES: Record<NightPaletteId, NightPaletteTokens> = {
  professional: {
    canvas: '#161d2e',
    surface: '#1e283c',
    muted: '#1a2234',
    elevated: '#28344c',
    border: '#3d4c66',
    text: '#f4f7fb',
    mutedText: '#a9b6c9',
    accent: '#2dd4bf',
  },
  deep: {
    canvas: '#0b1120',
    surface: '#111827',
    muted: '#0f172a',
    elevated: '#1e293b',
    border: '#2b3a52',
    text: '#e2e8f0',
    mutedText: '#94a3b8',
    accent: '#2dd4bf',
  },
  midnight: {
    canvas: '#0c1a1a',
    surface: '#132422',
    muted: '#0f1c1b',
    elevated: '#1a302d',
    border: '#2d4a45',
    text: '#ecfdf5',
    mutedText: '#99b8b2',
    accent: '#5eead4',
  },
};

export const DEFAULT_NIGHT_PALETTE_ID: NightPaletteId = 'professional';
export const DEFAULT_LOADER_APPEARANCE: LoaderAppearance = 'bright';

export function sanitizeNightPaletteId(value: unknown): NightPaletteId {
  return NIGHT_PALETTE_IDS.includes(value as NightPaletteId)
    ? (value as NightPaletteId)
    : DEFAULT_NIGHT_PALETTE_ID;
}

export function sanitizeLoaderAppearance(value: unknown): LoaderAppearance {
  return LOADER_APPEARANCES.includes(value as LoaderAppearance)
    ? (value as LoaderAppearance)
    : DEFAULT_LOADER_APPEARANCE;
}

export function resolveNightPalette(id: unknown): NightPaletteTokens {
  return NIGHT_PALETTES[sanitizeNightPaletteId(id)];
}

const NIGHT_STYLE_KEYS = [
  '--saas-canvas',
  '--saas-surface',
  '--saas-surface-muted',
  '--saas-elevated',
  '--saas-border',
  '--saas-text',
  '--saas-text-muted',
  '--saas-accent',
] as const;

type StyleLike = {
  setProperty?: (name: string, value: string) => void;
  removeProperty?: (name: string) => void;
};

export function applyNightPaletteToElement(
  style: StyleLike | null | undefined,
  tokens: NightPaletteTokens,
): void {
  if (!style || typeof style.setProperty !== 'function') return;
  style.setProperty('--saas-canvas', tokens.canvas);
  style.setProperty('--saas-surface', tokens.surface);
  style.setProperty('--saas-surface-muted', tokens.muted);
  style.setProperty('--saas-elevated', tokens.elevated);
  style.setProperty('--saas-border', tokens.border);
  style.setProperty('--saas-text', tokens.text);
  style.setProperty('--saas-text-muted', tokens.mutedText);
  style.setProperty('--saas-accent', tokens.accent);
}

export function clearNightPaletteFromElement(style: StyleLike | null | undefined): void {
  if (!style || typeof style.removeProperty !== 'function') return;
  for (const key of NIGHT_STYLE_KEYS) style.removeProperty(key);
}
